import { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { getAssignedRespondents, computeDemographicProfile } from '../../lib/segmentAssignment';
import type { AssignedRespondent, KMeansSegment } from '../../lib/segmentAssignment';
import { VALID_PRICES, SEGMENTS } from '../../data/conjointData';
import { formatNumber } from '../../lib/formatters';
import { GRID_PROPS, X_AXIS_PROPS, Y_AXIS_PROPS, TOOLTIP_STYLE, CHART_COLORS } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';
import { Filter } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

type FilterKey = 'gender' | 'ageRange' | 'income' | 'region' | 'goal' | 'spend' | 'hasWearable';

interface FilterOption { value: string; label: string; }

const FILTER_DEFS: { key: FilterKey; label: string; options: FilterOption[] }[] = [
  { key: 'gender', label: 'Gender', options: [
    { value: 'all', label: 'All' }, { value: 'Male', label: 'Male' }, { value: 'Female', label: 'Female' },
  ]},
  { key: 'ageRange', label: 'Age', options: [
    { value: 'all', label: 'All' }, { value: '18-34', label: '18-34' }, { value: '35-49', label: '35-49' }, { value: '50+', label: '50+' },
  ]},
  { key: 'income', label: 'Income', options: [
    { value: 'all', label: 'All' }, { value: 'under50k', label: '<$50K' }, { value: '50k-100k', label: '$50K-$100K' }, { value: '100k+', label: '$100K+' },
  ]},
  { key: 'region', label: 'Region', options: [
    { value: 'all', label: 'All' }, { value: 'Northeast', label: 'NE' }, { value: 'Southeast', label: 'SE' }, { value: 'Midwest', label: 'MW' }, { value: 'West', label: 'West' },
  ]},
  { key: 'goal', label: 'Goal', options: [
    { value: 'all', label: 'All' }, { value: 'weight', label: 'Weight' }, { value: 'health', label: 'Health' }, { value: 'strength', label: 'Strength' }, { value: 'nutrition', label: 'Nutrition' },
  ]},
  { key: 'spend', label: 'Spend', options: [
    { value: 'all', label: 'All' }, { value: 'nothing', label: 'Nothing' }, { value: 'under50', label: '<$50' }, { value: '50plus', label: '$50+' },
  ]},
  { key: 'hasWearable', label: 'Wearable', options: [
    { value: 'all', label: 'All' }, { value: 'yes', label: 'Has device' }, { value: 'no', label: 'No device' },
  ]},
];

function matchesFilter(r: AssignedRespondent, filters: Record<FilterKey, string>): boolean {
  if (filters.gender !== 'all' && r.gender !== filters.gender) return false;

  if (filters.ageRange !== 'all') {
    if (filters.ageRange === '18-34' && (r.age < 18 || r.age > 34)) return false;
    if (filters.ageRange === '35-49' && (r.age < 35 || r.age > 49)) return false;
    if (filters.ageRange === '50+' && r.age < 50) return false;
  }

  if (filters.income !== 'all') {
    const inc = r.income.toLowerCase();
    if (filters.income === 'under50k' && !inc.includes('under') && !inc.includes('less') && !inc.match(/\$[0-4]/)) return false;
    if (filters.income === '50k-100k' && !inc.includes('50,000') && !inc.includes('75,000')) return false;
    if (filters.income === '100k+' && !inc.includes('100,000') && !inc.includes('125,000') && !inc.includes('150,000') && !inc.includes('200,000') && !inc.includes('more')) return false;
  }

  if (filters.region !== 'all' && r.region !== filters.region) return false;

  if (filters.goal !== 'all') {
    const goal = r.primaryGoal.toLowerCase();
    if (filters.goal === 'weight' && !goal.includes('weight')) return false;
    if (filters.goal === 'health' && !goal.includes('health') && !goal.includes('longevity')) return false;
    if (filters.goal === 'strength' && !goal.includes('strength') && !goal.includes('muscle') && !goal.includes('fitness')) return false;
    if (filters.goal === 'nutrition' && !goal.includes('nutrition') && !goal.includes('diet') && !goal.includes('eating')) return false;
  }

  if (filters.spend !== 'all') {
    const spend = r.monthlySpend.toLowerCase();
    if (filters.spend === 'nothing' && !spend.includes('nothing') && !spend.includes('$0')) return false;
    if (filters.spend === 'under50' && (spend.includes('nothing') || spend.includes('$0') || spend.includes('$50') || spend.includes('$75') || spend.includes('$100') || spend.includes('more'))) {
      if (!spend.includes('$25') && !spend.includes('$1')) return false;
    }
    if (filters.spend === '50plus' && (spend.includes('nothing') || spend.includes('$0') || spend.includes('$25') || spend.includes('$1–'))) return false;
  }

  if (filters.hasWearable !== 'all') {
    const hasDevice = r.wearableDevices.length > 0 && !r.wearableDevices.every(d => d === 'None of the above');
    if (filters.hasWearable === 'yes' && !hasDevice) return false;
    if (filters.hasWearable === 'no' && hasDevice) return false;
  }

  return true;
}

function computeDemandCurve(respondents: AssignedRespondent[]): { price: number; rate: number }[] {
  return VALID_PRICES.map(price => {
    const key = String(price);
    let totalChoices = 0;
    let priceChoices = 0;
    for (const r of respondents) {
      const total = Object.values(r.priceDistribution).reduce((s, v) => s + v, 0) + r.noneCount;
      totalChoices += total;
      priceChoices += r.priceDistribution[key] || 0;
    }
    return { price, rate: totalChoices > 0 ? priceChoices / totalChoices : 0 };
  });
}

export function DemographicCrossTab() {
  const isMobile = useIsMobile();
  const allRespondents = useMemo(() => getAssignedRespondents(), []);

  const [filters, setFilters] = useState<Record<FilterKey, string>>({
    gender: 'all', ageRange: 'all', income: 'all', region: 'all', goal: 'all', spend: 'all', hasWearable: 'all',
  });

  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length;

  const filtered = useMemo(() =>
    allRespondents.filter(r => matchesFilter(r, filters)),
    [allRespondents, filters]
  );

  const overallCurve = useMemo(() => computeDemandCurve(allRespondents), [allRespondents]);
  const filteredCurve = useMemo(() => computeDemandCurve(filtered), [filtered]);

  const chartData = useMemo(() =>
    VALID_PRICES.map((price, i) => ({
      price: `$${price.toLocaleString()}`,
      overall: +(overallCurve[i].rate * 100).toFixed(2),
      filtered: +(filteredCurve[i].rate * 100).toFixed(2),
    })),
    [overallCurve, filteredCurve]
  );

  const segmentComposition = useMemo(() => {
    const counts: Record<KMeansSegment, number> = { 'Price-Sensitive Buyers': 0, 'Low-Interest Prospects': 0, 'Budget Seekers': 0 };
    for (const r of filtered) counts[r.kmeansSegment]++;
    const total = filtered.length || 1;
    return Object.entries(counts).map(([name, count]) => ({
      name: name as KMeansSegment,
      count,
      pct: +((count / total) * 100).toFixed(1),
      color: SEGMENTS.find(s => s.name === name)?.color || '#888',
    }));
  }, [filtered]);

  const profile = useMemo(() => computeDemographicProfile(filtered), [filtered]);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-1">
        <Filter size={16} className="text-chart-purple" />
        <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Demographic Cross-Tab</h3>
        <InfoButton helpKey="demographicCrossTab" size={13} />
      </div>
      <p className="text-sm text-muted mb-4">
        Filter respondents by demographics to see how demand curves, segment composition, and preferences shift.
      </p>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 mb-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
        {FILTER_DEFS.map(def => (
          <div key={def.key} className="flex items-center gap-1">
            <label className="text-micro text-muted font-medium">{def.label}</label>
            <select
              value={filters[def.key]}
              onChange={e => setFilters(prev => ({ ...prev, [def.key]: e.target.value }))}
              className="text-detail px-1.5 py-1 rounded border border-border/60 bg-transparent text-primary font-medium"
              style={{ backgroundColor: 'var(--color-card)' }}
            >
              {def.options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        ))}
        {activeFilterCount > 0 && (
          <button
            onClick={() => setFilters({ gender: 'all', ageRange: 'all', income: 'all', region: 'all', goal: 'all', spend: 'all', hasWearable: 'all' })}
            className="text-micro text-chart-teal hover:text-chart-teal/80 font-medium ml-1"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Results header */}
      <div className="flex items-center gap-4 mb-4 text-xs">
        <span className="text-primary font-semibold"><span className="font-mono">{formatNumber(filtered.length)}</span> respondents</span>
        <span className="text-muted">(<span className="font-mono">{((filtered.length / allRespondents.length) * 100).toFixed(1)}%</span> of total)</span>
        {activeFilterCount > 0 && (
          <span className="text-micro px-2 py-0.5 rounded-full bg-chart-purple/10 text-chart-purple font-medium">
            {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
          </span>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 text-muted text-sm">No respondents match the selected filters.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Demand curve comparison */}
          <div>
            <h4 className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">Demand Curve: Filtered vs. Overall</h4>
            <ResponsiveContainer width="100%" height={isMobile ? 180 : 220}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid {...GRID_PROPS} />
                <XAxis dataKey="price" {...X_AXIS_PROPS} />
                <YAxis {...Y_AXIS_PROPS} tickFormatter={v => `${v}%`} />
                <Tooltip {...TOOLTIP_STYLE} formatter={(v: number) => [`${v.toFixed(1)}%`, '']} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Line type="monotone" dataKey="overall" name="Overall" stroke="#9CA3AF" strokeDasharray="5 5" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="filtered" name="Filtered" stroke={CHART_COLORS.purple} strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Segment composition */}
          <div>
            <h4 className="text-xs text-muted uppercase tracking-wider font-semibold mb-2">Segment Composition</h4>
            <div className="space-y-2 mb-4">
              {segmentComposition.map(seg => (
                <div key={seg.name} className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
                  <span className="text-xs text-secondary flex-1">{seg.name}</span>
                  <span className="text-xs font-mono text-primary font-medium">{seg.pct}%</span>
                  <div className="w-20 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-surface)' }}>
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${seg.pct}%`, backgroundColor: seg.color }} />
                  </div>
                </div>
              ))}
            </div>

            {profile && (
              <>
                <h4 className="text-xs text-muted uppercase tracking-wider font-semibold mb-2 mt-4">Top Features (stated rank)</h4>
                <div className="space-y-1">
                  {profile.topFeatures.slice(0, 5).map((f, i) => (
                    <div key={f.feature} className="flex items-center justify-between text-micro">
                      <span className="text-secondary">{i + 1}. {f.feature}</span>
                      <span className="font-mono text-muted">avg #{f.avgRank}</span>
                    </div>
                  ))}
                </div>

                <h4 className="text-xs text-muted uppercase tracking-wider font-semibold mb-2 mt-4">Top Brands Considered</h4>
                <div className="flex flex-wrap gap-1">
                  {profile.brands.filter(b => b.label !== 'None of these').slice(0, 5).map(b => (
                    <span key={b.label} className="text-micro px-1.5 py-0.5 rounded-full border border-border/60 text-secondary">
                      {b.label} <span className="text-muted font-mono">{b.pct}%</span>
                    </span>
                  ))}
                </div>

                <h4 className="text-xs text-muted uppercase tracking-wider font-semibold mb-2 mt-4">Wearable Devices</h4>
                <div className="flex flex-wrap gap-1">
                  {profile.devices.filter(d => d.label !== 'None of the above').slice(0, 5).map(d => (
                    <span key={d.label} className="text-micro px-1.5 py-0.5 rounded-full border border-border/60 text-secondary">
                      {d.label} <span className="text-muted font-mono">{d.pct}%</span>
                    </span>
                  ))}
                </div>

                <h4 className="text-xs text-muted uppercase tracking-wider font-semibold mb-2 mt-4">Attitudes (% agree/strongly agree)</h4>
                <div className="space-y-1">
                  {profile.attitudes.map(a => (
                    <div key={a.statement} className="flex items-center justify-between text-micro">
                      <span className="text-muted truncate pr-2" title={a.statement}>{shortenStatement(a.statement)}</span>
                      <span className={`font-mono font-medium shrink-0 ${a.agreeRate >= 60 ? 'text-metric-positive' : a.agreeRate >= 40 ? 'text-primary' : 'text-metric-negative'}`}>
                        {a.agreeRate}%
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function shortenStatement(s: string): string {
  if (s.includes('DNA')) return 'DNA fitness';
  if (s.includes('Biometric')) return 'Biometric tracking';
  if (s.includes('Personalized')) return 'Personalized plans';
  if (s.includes('Recovery')) return 'Recovery content';
  if (s.includes('Nutrition')) return 'Nutrition guidance';
  return s.slice(0, 25);
}
